import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../Badge';
import { StatCard } from '../StatCard';
import { formatCents, formatDate } from '../../utils/format';
import { enumLabel } from '../../utils/i18nLabels';
import { openFlowIaAssistant } from '../../utils/openFlowIaAssistant';
import { buildEmployeeFlowInsight, countPendingDocs } from '../../utils/employeeHubData';
import { formatMsg } from '../../utils/formatMsg';

const QUICK_LINKS = [
  { to: '/leaves', icon: '◷', labelKey: 'hub93.quickLeave' },
  { to: '/payroll-receipts', icon: '▤', labelKey: 'hub93.quickPayslips' },
  { to: '/records', icon: '▣', labelKey: 'hub93.quickUpload' },
  { to: '/attendance', icon: '⏱', labelKey: 'hub93.quickAttendance' },
];

const NAV_PILLS = [
  { to: '/attendance', labelKey: 'hub93.navShift' },
  { to: '/leaves', labelKey: 'hub93.navVacation' },
  { to: '/records', labelKey: 'hub93.navDocs' },
  { to: '/expense-reports', labelKey: 'hub93.navRequests' },
];

/**
 * @param {object} props
 */
export function EmployeeHubView({
  t,
  data,
  emp,
  name,
  roleLine,
  empStatusVariant,
  managerStats,
  managerLoading,
  managerError,
  showManagerBlock,
  persona,
  commsPreview,
  commsLoading,
  commsError,
  hubCulture,
  hubCultureLoading,
  hubCultureError,
}) {
  const pf = data.personal_finance_summary;
  const pendingDocs = countPendingDocs(data);
  const flowPack = useMemo(
    () => buildEmployeeFlowInsight(data, managerStats, persona, t),
    [data, managerStats, persona, t]
  );

  const newsItems = useMemo(() => {
    const items = [];
    (commsPreview || []).slice(0, 3).forEach((c) => {
      items.push({ id: `c-${c.id}`, type: 'comm', title: c.title, to: '/communications' });
    });
    (data.awards || []).slice(0, 2).forEach((a) => {
      items.push({ id: `a-${a.id}`, type: 'award', title: a.title, to: '/awards' });
    });
    return items.slice(0, 4);
  }, [commsPreview, data.awards]);

  return (
    <div className="page-stack employee-hub">
      <header className="employee-hub__hero panel panel--elevated">
        <div className="portal-app-header__avatar" aria-hidden>
          {(emp.first_name?.[0] || '?') + (emp.last_name?.[0] || '')}
        </div>
        <div className="employee-hub__hero-body">
          <h1 className="employee-hub__title">{formatMsg(t('hub93.greeting'), { name })}</h1>
          <p className="employee-hub__role">{roleLine}</p>
          <nav className="employee-hub__nav-pills" aria-label={t('hub93.navAria')}>
            {NAV_PILLS.map((p) => (
              <Link key={p.to} to={p.to} className="employee-hub__nav-pill">
                {t(p.labelKey)}
              </Link>
            ))}
          </nav>
          <Badge variant={empStatusVariant(emp.status)}>{enumLabel(t, 'employeeStatus', emp.status)}</Badge>
        </div>
      </header>

      <section className="employee-hub__quick" aria-labelledby="hub-quick-title">
        <h2 id="hub-quick-title" className="dash-section-title dash-section-title--compact">
          {t('hub93.quickTitle')}
        </h2>
        <div className="employee-hub__quick-grid">
          {QUICK_LINKS.map((q) => (
            <Link key={q.to} to={q.to} className="employee-hub__quick-card panel">
              <span className="employee-hub__quick-icon" aria-hidden>
                {q.icon}
              </span>
              <span>{t(q.labelKey)}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="panel panel--elevated employee-hub__status" aria-labelledby="hub-status-title">
        <h2 id="hub-status-title" className="dash-section-title dash-section-title--compact">
          {t('hub93.statusTitle')}
        </h2>
        <div className="grid-stats employee-hub__status-grid">
          <StatCard
            layout="center"
            label={t('hub93.statusVacationAvail')}
            value={data.upcoming_leave ? t('hub93.statusScheduled') : t('hub93.statusNone')}
            hint={
              data.upcoming_leave
                ? `${formatDate(data.upcoming_leave.start_date)} → ${formatDate(data.upcoming_leave.end_date)}`
                : undefined
            }
          />
          <StatCard
            layout="center"
            label={t('hub93.statusNextLeave')}
            value={data.upcoming_leave ? formatDate(data.upcoming_leave.start_date) : '—'}
          />
          <StatCard layout="center" label={t('hub93.statusDocsPending')} value={pendingDocs} tone={pendingDocs ? 'warning' : undefined} />
          <StatCard
            layout="center"
            label={t('hub93.statusAttendanceToday')}
            value={t('hub93.statusAttendanceOpen')}
            hint={t('hub93.statusAttendanceHint')}
          />
        </div>
        {pf ? (
          <p className="panel__muted text-sm employee-hub__finance-hint">
            {t('personalFinance.balance')}: {formatCents(pf.estimated_balance_cents, 'ARS')}
          </p>
        ) : null}
      </section>

      <section className="panel panel--elevated employee-hub__news" aria-labelledby="hub-news-title">
        <div className="dash-section-head dash-section-head--row">
          <h2 id="hub-news-title" className="dash-section-title dash-section-title--compact">
            {t('hub93.newsTitle')}
          </h2>
          <Link to="/communications" className="btn btn--sm btn--ghost">
            {t('hub93.newsSeeAll')}
          </Link>
        </div>
        {commsLoading ? (
          <p className="panel__muted">{t('common.loading')}</p>
        ) : commsError ? (
          <p className="panel__muted">{t('hub93.newsError')}</p>
        ) : newsItems.length === 0 ? (
          <p className="panel__muted">{t('hub93.newsEmpty')}</p>
        ) : (
          <ul className="dash-activity-feed__list employee-hub__news-list">
            {newsItems.map((item) => (
              <li key={item.id} className="dash-activity-feed__row">
                <span className="dash-activity-feed__icon" aria-hidden>
                  {item.type === 'award' ? '★' : '✉'}
                </span>
                <span className="dash-activity-feed__title">{item.title}</span>
                <Link to={item.to} className="dash-activity-feed__link">
                  {t('hub93.newsOpen')}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel panel--elevated employee-hub__culture" aria-labelledby="hub-culture-title">
        <div className="dash-section-head dash-section-head--row">
          <h2 id="hub-culture-title" className="dash-section-title dash-section-title--compact">
            {t('hub93.cultureTitle')}
          </h2>
          <Link to="/feed" className="btn btn--sm btn--ghost">
            {t('hub93.cultureFeedLink')}
          </Link>
        </div>
        {hubCultureLoading ? (
          <p className="panel__muted">{t('common.loading')}</p>
        ) : hubCultureError ? (
          <p className="panel__muted">{t('hub93.cultureError')}</p>
        ) : (
          <div className="employee-hub__culture-grid">
            <div className="employee-hub__culture-block">
              <h3 className="employee-hub__culture-sub">{t('hub93.cultureEvents')}</h3>
              {(hubCulture?.upcoming_events || []).length === 0 ? (
                <p className="panel__muted text-sm">{t('hub93.cultureEventsEmpty')}</p>
              ) : (
                <ul className="employee-hub__culture-list">
                  {hubCulture.upcoming_events.slice(0, 3).map((ev) => (
                    <li key={ev.id}>
                      <strong>{ev.title}</strong>
                      <span className="panel__muted text-sm"> · {formatDate(ev.event_date)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="employee-hub__culture-block">
              <h3 className="employee-hub__culture-sub">{t('hub93.cultureRecognitions')}</h3>
              {(hubCulture?.recent_recognitions || []).length === 0 ? (
                <p className="panel__muted text-sm">{t('hub93.cultureRecognitionsEmpty')}</p>
              ) : (
                <ul className="employee-hub__culture-list">
                  {hubCulture.recent_recognitions.slice(0, 3).map((r, i) => (
                    <li key={`${r.employee_name}-${i}`}>
                      <strong>{r.employee_name}</strong>
                      <span className="panel__muted text-sm"> — {r.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="employee-hub__culture-block">
              <h3 className="employee-hub__culture-sub">{t('hub93.cultureAnniversaries')}</h3>
              {(hubCulture?.upcoming_anniversaries || []).length === 0 ? (
                <p className="panel__muted text-sm">{t('hub93.cultureAnniversariesEmpty')}</p>
              ) : (
                <ul className="employee-hub__culture-list">
                  {hubCulture.upcoming_anniversaries.slice(0, 3).map((a, i) => (
                    <li key={`${a.name}-${i}`}>
                      {a.name}
                      <span className="panel__muted text-sm"> · {formatDate(a.date)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="employee-hub__culture-block">
              <h3 className="employee-hub__culture-sub">{t('hub93.cultureFeed')}</h3>
              {hubCulture?.recent_feed_count > 0 ? (
                <p className="text-sm">
                  {hubCulture.recent_feed_count} {t('hub93.cultureFeedLink').toLowerCase()}
                </p>
              ) : (
                <p className="panel__muted text-sm">{t('hub93.cultureFeedEmpty')}</p>
              )}
            </div>
          </div>
        )}
      </section>

      {showManagerBlock ? (
        <section className="panel panel--elevated employee-hub__team" aria-labelledby="hub-team-title">
          <h2 id="hub-team-title" className="dash-section-title dash-section-title--compact">
            {t('hub93.teamTitle')}
          </h2>
          {managerLoading ? (
            <p className="panel__muted">{t('common.loading')}</p>
          ) : managerError ? (
            <p className="panel__muted">{t('hub93.teamError')}</p>
          ) : (
            <div className="grid-stats employee-hub__team-grid">
              <StatCard layout="center" label={t('hub93.teamMembers')} value={managerStats?.teamSize ?? 0} />
              <StatCard
                layout="center"
                label={t('hub93.teamAbsences')}
                value={managerStats?.pendingLeaves ?? 0}
                tone={managerStats?.pendingLeaves ? 'warning' : undefined}
              />
              <StatCard layout="center" label={t('hub93.teamPendingExp')} value={managerStats?.pendingExpenses ?? 0} />
            </div>
          )}
          <Link to="/leaves" className="btn btn--primary btn--sm">
            {t('hub93.teamCtaApprovals')}
          </Link>
        </section>
      ) : null}

      <section className="panel panel--elevated dash-flow-ia-module employee-hub__flow" aria-labelledby="hub-flow-title">
        <h2 id="hub-flow-title" className="dash-section-title dash-section-title--compact">
          {t('dashboard93.flowIaLabel')}
        </h2>
        <p className="dash-flow-ia-module__insight">{flowPack.insight}</p>
        <p className="panel__muted text-sm">{flowPack.action}</p>
        <div className="dash-flow-ia-module__actions">
          {flowPack.to ? (
            <Link to={flowPack.to} className="btn btn--primary btn--sm">
              {t('hub93.flowGo')}
            </Link>
          ) : null}
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => openFlowIaAssistant()}>
            {t('dashboard93.flowIaSeeMore')}
          </button>
        </div>
      </section>

      {(data.alerts || []).length > 0 ? (
        <section className="employee-hub__alerts" aria-label={t('hub93.alertsAria')}>
          {data.alerts.map((a, i) => (
            <div key={i} className="banner banner--info">
              {a}
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}
